import React, { useEffect, useMemo, useState } from 'react';
import { Clock, PauseCircle } from 'lucide-react';
import { formatDateTime } from '../lib/dateFormat';

const CountdownSegment = ({ value, unit, testId }) => (
  <div
    className="flex flex-col items-center min-w-[68px] sm:min-w-[92px] px-3 sm:px-5 py-3 sm:py-4 rounded-2xl bg-white/95 shadow-lg border border-white/60"
    data-testid={testId}
  >
    <div className="text-3xl sm:text-5xl tabular-nums text-[#0F172A]" data-testid={`${testId}-value`}>
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-neutral-500 mt-1">{unit}</div>
  </div>
);

/**
 * Full-screen block page rendered on the branch new-order form when
 * the signed-in branch falls inside an active cut-off window. Shows
 * the company logo, a friendly explanation, the exact reopen
 * timestamp, and a live seconds-precision countdown so branch users
 * see the wait shrinking in real time.
 *
 * Props:
 *   toIso       — ISO string of the window's `to_ts`
 *   label       — optional human-readable label ("Q1 Stock Take")
 *   logoUrl     — company logo URL from site_settings
 *   onExpired   — callback fired when the countdown reaches zero so
 *                 the parent can flip back to the normal form
 */
const CutoffBlockPage = ({ toIso, label, logoUrl, onExpired }) => {
  const target = useMemo(() => (toIso ? new Date(toIso).getTime() : 0), [toIso]);
  const [remaining, setRemaining] = useState(Math.max(0, target - Date.now()));

  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => {
      const left = Math.max(0, target - Date.now());
      setRemaining(left);
      if (left <= 0) {
        clearInterval(id);
        // Small delay lets React repaint the "00:00:00:00" frame
        // before we ask the parent to unmount us.
        setTimeout(() => onExpired?.(), 400);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target, onExpired]);

  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const _fmtDT = (iso) => formatDateTime(iso);

  const Segment = CountdownSegment;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4 py-8 bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0F172A]"
      data-testid="branch-cutoff-block"
    >
      {/* Soft radial glow behind the card */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[70vmin] h-[70vmin] rounded-full bg-blue-400 blur-[110px]" />
      </div>

      <div className="relative w-full max-w-2xl bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl shadow-2xl px-6 sm:px-10 py-8 sm:py-10 text-center">
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Company logo"
            className="h-14 sm:h-20 mx-auto mb-3 object-contain drop-shadow-lg"
            data-testid="branch-cutoff-logo"
          />
        )}
        <div
          className="text-white text-xl sm:text-2xl mb-6 tracking-wide"
          data-testid="branch-cutoff-company-name"
        >
          MOHAMMED RIAZ &amp; PARTNER LLC
        </div>

        <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 text-amber-300 px-3 py-1 text-[11px] uppercase tracking-wider mb-4 border border-amber-500/30">
          <PauseCircle className="w-3.5 h-3.5" />
          Ordering Paused
        </div>

        <h1 className="text-white text-2xl sm:text-3xl mb-2">
          Orders are temporarily unavailable
        </h1>
        {label && (
          <div className="text-white/60 text-sm mb-3" data-testid="branch-cutoff-label">
            {label}
          </div>
        )}
        <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md mx-auto mb-8">
          Our ordering system is briefly closed for your branch. We&rsquo;ll be back at the time shown below — no action needed on your side.
        </p>

        <div className="flex justify-center gap-2 sm:gap-3 mb-6" data-testid="branch-cutoff-countdown">
          <Segment value={days} unit="Days" testId="branch-cutoff-days" />
          <Segment value={hours} unit="Hours" testId="branch-cutoff-hours" />
          <Segment value={minutes} unit="Mins" testId="branch-cutoff-minutes" />
          <Segment value={seconds} unit="Secs" testId="branch-cutoff-seconds" />
        </div>

        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white/80 px-4 py-2 text-xs sm:text-sm border border-white/10">
          <Clock className="w-4 h-4" />
          Reopens <span className="text-white ml-1" data-testid="branch-cutoff-until">{_fmtDT(toIso)}</span>
        </div>
      </div>
    </div>
  );
};

export default CutoffBlockPage;
